import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const MoleculeModeFloating = ({ onModeChange }) => {
  const [selectedMode, setSelectedMode] = useState("custom"); // "custom" 또는 "auto"
  const [tooltipVisible, setTooltipVisible] = useState(null); // null, "custom", "auto"
  const hoverTimeoutRef = useRef(null);

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    if (onModeChange) {
      onModeChange(mode);
    }
  };

  const handleMouseEnter = (mode) => {
    hoverTimeoutRef.current = setTimeout(() => {
      setTooltipVisible(mode);
    }, 1000);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setTooltipVisible(null);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StyledFloatingContainer>
      <StyledLeftSection>
        <StyledIconContainer>
          <StyledDisplayIcon>
            {selectedMode === "custom" ? (
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="48"
                  height="48"
                  rx="24"
                  fill="#256EF4"
                  fillOpacity="0.12"
                />
                <path
                  d="M14.0947 22.835H34.667C35.2913 22.8352 35.7976 23.3415 35.7979 23.9658V34.252C35.7979 34.8764 35.2914 35.3826 34.667 35.3828H14.0947C13.4703 35.3826 12.9639 34.8764 12.9639 34.252V23.9658C12.9641 23.3415 13.4705 22.8352 14.0947 22.835ZM12.9639 26.5576H35.7979"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                />
                <path
                  d="M13.3804 17.1094V11.1094"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                />
                <path
                  d="M35.3813 17.1094V11.1094"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                />
                <path
                  d="M24.3809 16.1094V12.1094"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                />
                <path
                  d="M14.1044 14.1094L34.6758 14.1094"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                />
              </svg>
            ) : (
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="48"
                  height="48"
                  rx="24"
                  fill="#256EF4"
                  fillOpacity="0.12"
                />
                <path
                  d="M16.2861 20.5723L31.7147 30.858"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                />
                <path
                  d="M13.7139 12.583H34.2861C34.9104 12.5832 35.4168 13.0896 35.417 13.7139V33.4287C35.4169 34.0531 34.9105 34.5593 34.2861 34.5596H13.7139C13.0895 34.5593 12.5831 34.0531 12.583 33.4287V13.7139C12.5832 13.0896 13.0896 12.5832 13.7139 12.583ZM12.583 16.7734H35.417"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                />
                <path
                  d="M19.7147 20.5723H16.2861V24.0008"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M31.7139 27.4289L31.7139 30.8574L28.2853 30.8574"
                  stroke="#256EF4"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </StyledDisplayIcon>
        </StyledIconContainer>
        <StyledTitle>디스플레이 모드 선택</StyledTitle>
      </StyledLeftSection>

      <StyledDivider />

      <StyledModeSelector>
        <StyledSlidingBackground selectedMode={selectedMode} />
        <StyledButtonContainer>
          <StyledModeButton
            isSelected={selectedMode === "custom"}
            onClick={() => handleModeSelect("custom")}
            onMouseEnter={() => handleMouseEnter("custom")}
            onMouseLeave={handleMouseLeave}
          >
            내 디스플레이
          </StyledModeButton>
          {tooltipVisible === "custom" && (
            <StyledTooltipContainer>
              <StyledTooltipContent>
                <StyledTooltipTitle>내 디스플레이란?</StyledTooltipTitle>
                <StyledTooltipDescription>
                  사전에 설정한 화면
                  <br />
                  비율로 콘텐츠가
                  <br />
                  동일하게 유지됩니다.
                </StyledTooltipDescription>
              </StyledTooltipContent>
              <StyledTooltipArrow />
            </StyledTooltipContainer>
          )}
        </StyledButtonContainer>
        <StyledButtonContainer>
          <StyledModeButton
            isSelected={selectedMode === "auto"}
            onClick={() => handleModeSelect("auto")}
            onMouseEnter={() => handleMouseEnter("auto")}
            onMouseLeave={handleMouseLeave}
          >
            화면 자동맞춤
          </StyledModeButton>
          {tooltipVisible === "auto" && (
            <StyledTooltipContainer>
              <StyledTooltipContent>
                <StyledTooltipTitle>화면 자동맞춤이란?</StyledTooltipTitle>
                <StyledTooltipDescription>
                  보는 화면에 맞게
                  <br />
                  콘텐츠가 유동적으로
                  <br />
                  맞춰집니다.
                </StyledTooltipDescription>
              </StyledTooltipContent>
              <StyledTooltipArrow />
            </StyledTooltipContainer>
          )}
        </StyledButtonContainer>
      </StyledModeSelector>
    </StyledFloatingContainer>
  );
};

export default MoleculeModeFloating;

// Styled Components
const StyledFloatingContainer = styled.div`
  width: 732px;
  height: 64px;
  padding: 0 0px;
  background: white;
  border-radius: 32px;
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.2);
  display: flex;
  gap: 24px;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  position: relative;
`;

const StyledLeftSection = styled.div`
  padding-right: 0px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
`;

const StyledIconContainer = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding-left: 20px;
`;

const StyledDisplayIcon = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 48px !important;
    height: 48px !important;
    min-width: 48px;
    min-height: 48px;
    flex-shrink: 0;
  }
`;

const StyledTitle = styled.div`
  color: #262626;
  font-size: 24px;
  font-family: "Pretendard", sans-serif;
  font-weight: 500;
  line-height: 36px;
  margin-right: 24px;
  margin-left: 10px;
`;

const StyledDivider = styled.div`
  width: 1px;
  height: 64px;
  background: #f4f4f5;
  flex-shrink: 0;
  align-self: center;
`;

const StyledModeSelector = styled.div`
  height: 48px;
  padding: 4px;
  background: #f4f4f5;
  border-radius: 24px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
`;

const StyledSlidingBackground = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 184px;
  height: 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.16);
  transform: translateX(
    ${(props) => (props.selectedMode === "auto" ? "184px" : "0px")}
  );
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
`;

const StyledModeButton = styled.button`
  width: 184px;
  height: 40px;
  padding: 0;
  background: transparent;
  border-radius: 20px;
  border: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: color 0.3s ease-in-out;
  position: relative;
  z-index: 2;

  color: ${(props) => (props.isSelected ? "#2563eb" : "#a3a3a3")};
  font-size: 20px;
  font-family: "Pretendard", sans-serif;
  font-weight: 500;
  line-height: 32px;
  text-align: center;

  &:hover {
    color: ${(props) => (props.isSelected ? "#2563eb" : "#666666")};
  }
`;

const StyledButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledTooltipContainer = styled.div`
  position: absolute;
  top: -130px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`;

const StyledTooltipContent = styled.div`
  padding: 12px 20px;
  background: rgb(40, 43, 46);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  min-width: 165px;
  letter-spacing: -0.03em;
`;

const StyledTooltipTitle = styled.span`
  color: white;
  font-size: 16px;
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  line-height: 24px;
  word-wrap: break-word;
`;

const StyledTooltipDescription = styled.span`
  color: white;
  font-size: 14px;
  font-family: "Pretendard", sans-serif;
  font-weight: 300;
  line-height: 21px;
  word-wrap: break-word;
  white-space: pre-line;
  text-align: left;
  opacity: 0.8;
`;

const StyledTooltipArrow = styled.div`
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-top: 12px solid #3b3f45;
  margin-top: -1px;
`;
